import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import StarIcon from "@material-ui/icons/Star";
import MessageTemplateApi, {
  AVAILABLE_FIELDS,
  FIELD_CATEGORIES,
  AUDIENCE_LABELS,
  MessageAudience,
  MessageTemplateRes,
  CreateMessageTemplateReq,
  isPlanAllowingUrlPage,
} from "api/MessageTemplateApi";

interface FormState {
  messageTemplateId: number | null;
  audience: MessageAudience;
  name: string;
  subject: string;
  body: string;
  useUrlPage: boolean; // visit Phase 3: URL 先ページ機能 ON/OFF
  urlPageBody: string;
  urlPageTitle: string;
  enabledFields: string[];
  isDefault: boolean;
}

/** SMS 1 通の文字数上限 (日本語 SMS、 UTF-16 換算で 70 文字) */
const SMS_CHARS_PER_SEGMENT = 70;
/** SMS 1 通あたりの単価 (円) */
const SMS_UNIT_PRICE = 15;
/** visit Phase 3: 短縮 URL の想定長さ (https://t.no-tel.com/v1/s/<12 chars> = 38 chars) */
const SHORT_URL_LENGTH = 38;
/** URL 短縮 ON 時の SMS 本文 目安上限 (置換後、 URL 含む) */
const URL_SMS_SOFT_LIMIT = 75;
/** SMS 本文の URL_PAGE プレースホルダ (orderMessage service.ts と整合) */
const URL_PAGE_PLACEHOLDER = "{URL_PAGE}";

/**
 * プレースホルダごとの想定展開文字数 (見積もり用)。
 * 送信時は server 側で実値置換 → 実コストは renderedBody.length で算出されるが、
 * マスター画面では実値が無いため平均的な値で長さを仮算出する。
 */
const PLACEHOLDER_AVG_LENGTH: Record<string, number> = {
  guestName: 6,
  guestKana: 8,
  guestTel: 13,
  guestCategory: 4,
  guestMemo: 0,
  shopName: 8,
  castName: 5,
  castClassName: 5,
  courseName: 10,
  totalTime: 3,
  orderDate: 16,
  orderPlanInTime: 16,
  orderPlanOutTime: 16,
  orderActualInTime: 16,
  orderActualEndTime: 16,
  cleaningEndTime: 16,
  roomName: 6,
  additionalTime: 2,
  totalFee: 7,
  paymentType: 4,
  optionName: 0,
  discountName: 0,
  areaName: 0,
  hotelName: 0,
  orderAddress: 0,
  orderMemo: 0,
  orderCreatedBy: 0,
  orderUpdatedBy: 0,
};

/** 本文に含まれる {{placeholder}} を平均文字数に仮展開して length を返す */
function estimateSmsLength(body: string): number {
  return body.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    "あ".repeat(PLACEHOLDER_AVG_LENGTH[key] ?? 0)
  ).length;
}

/**
 * visit Phase 3: URL 短縮 ON 時の SMS 想定長さ (本文 + 短縮 URL)。
 * - {URL_PAGE} (10 chars) → 短縮 URL (38 chars) 置換後の長さで判定
 * - {{placeholder}} は平均値で展開
 */
function estimateSmsLengthWithUrl(body: string): number {
  // URL_PAGE プレースホルダを短縮 URL 長で置換
  const replacedUrl = body.split(URL_PAGE_PLACEHOLDER).join("X".repeat(SHORT_URL_LENGTH));
  return estimateSmsLength(replacedUrl);
}

const emptyForm = (audience: MessageAudience): FormState => ({
  messageTemplateId: null,
  audience,
  name: "",
  subject: "",
  body: "",
  useUrlPage: false,
  urlPageBody: "",
  urlPageTitle: "",
  enabledFields: AVAILABLE_FIELDS.map((f) => f.key),
  isDefault: false,
});

const MessageTemplate: React.FC = () => {
  const companyId = useSelector(
    (state: { account: { staff: { companyId: number } } }) =>
      state.account.staff.companyId
  );

  // 現在ログイン中の会社のプラン + 業態 (URL 短縮機能の制限判定に使用)
  const companyPlan = useSelector(
    (state: { account: { staff: { company?: { companyGroup?: { plan?: string } } } } }) =>
      state.account.staff.company?.companyGroup?.plan
  );
  const companyBusinessType = useSelector(
    (state: {
      account: {
        staff: { company?: { companyGroup?: { businessType?: string } } };
      };
    }) => state.account.staff.company?.companyGroup?.businessType
  );
  // #473: URL 短縮は会社単位フラグ (urlPageSmsEnabled) + starter/premium プランで利用可 (業態問わず統一)
  const companyUrlPageSmsEnabled = useSelector(
    (state: {
      account: { staff: { company?: { urlPageSmsEnabled?: boolean } } };
    }) => state.account.staff.company?.urlPageSmsEnabled
  );
  const canUseUrlPage =
    isPlanAllowingUrlPage(companyPlan) && !!companyUrlPageSmsEnabled;

  const [tab, setTab] = useState<MessageAudience>("customer");
  const [templates, setTemplates] = useState<MessageTemplateRes[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<FormState | null>(null);
  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);
  const urlPageBodyRef = React.useRef<HTMLTextAreaElement | null>(null);
  // 最後にフォーカスした textarea を覚えておく (プレースホルダ挿入先判定)
  const [focusedField, setFocusedField] = useState<"body" | "urlPageBody">(
    "body"
  );

  const fetchTemplates = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const list = await MessageTemplateApi.list(companyId);
      setTemplates(list);
    } catch (e) {
      console.error("テンプレート取得失敗", e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filtered = useMemo(
    () => templates.filter((t) => t.audience === tab),
    [templates, tab]
  );

  const handleNew = () => {
    setEditing(emptyForm(tab));
  };

  const handleEdit = (t: MessageTemplateRes) => {
    setEditing({
      messageTemplateId: t.messageTemplateId,
      audience: t.audience,
      name: t.name,
      subject: t.subject ?? "",
      body: t.body,
      useUrlPage: !!t.urlPageBody,
      urlPageBody: t.urlPageBody ?? "",
      urlPageTitle: t.urlPageTitle ?? "",
      enabledFields: t.enabledFields ?? AVAILABLE_FIELDS.map((f) => f.key),
      isDefault: t.isDefault,
    });
    setFocusedField("body");
  };

  const handleDelete = async (t: MessageTemplateRes) => {
    if (t.isDefault) {
      window.alert(
        "既定テンプレートは削除できません。 先に他テンプレートを既定に変更してください。"
      );
      return;
    }
    if (
      !window.confirm(
        `テンプレート「${t.name}」を削除します。 よろしいですか?`
      )
    )
      return;
    try {
      await MessageTemplateApi.delete(companyId, t.messageTemplateId);
      fetchTemplates();
    } catch (e) {
      console.error("削除失敗", e);
      window.alert("削除に失敗しました。");
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) {
      window.alert("テンプレート名は必須です。");
      return;
    }
    if (!editing.body.trim()) {
      window.alert("本文は必須です。");
      return;
    }
    // visit Phase 3: URL 先ページ機能のバリデーション
    if (editing.useUrlPage) {
      if (!editing.urlPageBody.trim()) {
        window.alert("URL 先ページ本文を入力してください (または URL 先ページ機能を OFF にしてください)。");
        return;
      }
      if (!editing.body.includes("{URL_PAGE}")) {
        window.alert(
          "SMS 本文に {URL_PAGE} プレースホルダが含まれていません。 SMS 本文中で URL 先ページのリンクを表示する位置に {URL_PAGE} を挿入してください。"
        );
        return;
      }
    }
    try {
      const payload: CreateMessageTemplateReq = {
        audience: editing.audience,
        name: editing.name.trim(),
        subject: editing.subject.trim() || null,
        body: editing.body,
        urlPageBody: editing.useUrlPage ? editing.urlPageBody : null,
        urlPageTitle: editing.useUrlPage
          ? editing.urlPageTitle.trim() || null
          : null,
        enabledFields: editing.enabledFields,
        isDefault: editing.isDefault,
      };
      if (editing.messageTemplateId == null) {
        await MessageTemplateApi.create(companyId, payload);
      } else {
        await MessageTemplateApi.update(
          companyId,
          editing.messageTemplateId,
          payload
        );
      }
      setEditing(null);
      fetchTemplates();
    } catch (e) {
      console.error("保存失敗", e);
      window.alert("保存に失敗しました。");
    }
  };

  const insertPlaceholder = (key: string) => {
    if (!editing) return;
    const token = `{{${key}}}`;
    // visit Phase 3: 最後にフォーカスした textarea (body / urlPageBody) へ挿入
    const targetField = focusedField;
    const el =
      targetField === "urlPageBody" ? urlPageBodyRef.current : bodyRef.current;
    const currentText =
      targetField === "urlPageBody" ? editing.urlPageBody : editing.body;
    if (!el) {
      setEditing({
        ...editing,
        [targetField]: currentText + token,
      } as FormState);
      return;
    }
    const start = el.selectionStart ?? currentText.length;
    const end = el.selectionEnd ?? currentText.length;
    const next = currentText.slice(0, start) + token + currentText.slice(end);
    setEditing({ ...editing, [targetField]: next } as FormState);
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + token.length;
      el.selectionEnd = start + token.length;
    }, 0);
  };

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        メッセージテンプレート
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        受注画面から顧客 / キャスト / スタッフへ送信するメッセージのテンプレートを管理します。
        プレースホルダ <code>{"{{guestName}}"}</code> 等は送信時に Order の情報に置換されます。
      </Typography>

      <Paper style={{ marginTop: 16 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as MessageAudience)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="customer" label="顧客向け" />
          <Tab value="cast" label="キャスト向け" />
          {/* #473: delivery はスタッフ向け→ドライバー向けに置き換え */}
          {companyBusinessType === "delivery" ? (
            <Tab value="driver" label="ドライバー向け" />
          ) : (
            <Tab value="staff" label="スタッフ向け" />
          )}
        </Tabs>

        <Box p={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNew}
            disabled={loading}
          >
            新規テンプレート
          </Button>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 40 }} />
              <TableCell>テンプレート名</TableCell>
              <TableCell>件名</TableCell>
              <TableCell>本文プレビュー</TableCell>
              <TableCell style={{ width: 120 }} align="center">
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box py={3}>
                    <Typography variant="body2" color="textSecondary">
                      テンプレートがありません。
                      「新規テンプレート」 から追加してください。
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t) => (
                <TableRow key={t.messageTemplateId} hover>
                  <TableCell>
                    {t.isDefault ? (
                      <Tooltip title="既定">
                        <StarIcon style={{ color: "#fbc02d" }} />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.subject || "—"}</TableCell>
                  <TableCell
                    style={{
                      maxWidth: 360,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.body.slice(0, 60).replace(/\n/g, " ")}
                    {t.body.length > 60 ? "…" : ""}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(t)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 編集ダイアログ - 横並び 2 ペイン */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {editing?.messageTemplateId == null ? "新規" : "編集"} —{" "}
          {editing ? AUDIENCE_LABELS[editing.audience] : ""}
        </DialogTitle>
        <DialogContent dividers>
          {editing && (
            <Box display="flex" style={{ gap: 16, minHeight: 760 }}>
              {/* 左ペイン: 編集 */}
              <Box flex={2} display="flex" flexDirection="column" style={{ gap: 12 }}>
                <TextField
                  label="テンプレート名"
                  value={editing.name}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                  fullWidth
                  required
                  inputProps={{ maxLength: 64 }}
                />
                <TextField
                  label="件名 (メール用、 SMS は無視)"
                  value={editing.subject}
                  onChange={(e) =>
                    setEditing({ ...editing, subject: e.target.value })
                  }
                  fullWidth
                  inputProps={{ maxLength: 255 }}
                />
                <TextField
                  label={
                    editing.useUrlPage
                      ? "SMS 本文 (70 字以内推奨、 {URL_PAGE} で URL 挿入)"
                      : "本文"
                  }
                  value={editing.body}
                  onChange={(e) =>
                    setEditing({ ...editing, body: e.target.value })
                  }
                  onFocus={() => setFocusedField("body")}
                  fullWidth
                  multiline
                  rows={editing.useUrlPage ? 6 : 28}
                  required
                  inputRef={bodyRef}
                />
                {editing.audience === "customer" ? (
                  (() => {
                    const estimatedLength = editing.useUrlPage
                      ? estimateSmsLengthWithUrl(editing.body)
                      : estimateSmsLength(editing.body);
                    const segments = Math.max(
                      1,
                      Math.ceil(estimatedLength / SMS_CHARS_PER_SEGMENT)
                    );
                    const cost = segments * SMS_UNIT_PRICE;
                    // URL 短縮 ON 時の soft limit 警告
                    const isOverSoftLimit =
                      editing.useUrlPage && estimatedLength > URL_SMS_SOFT_LIMIT;
                    return (
                      <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        style={{
                          gap: 12,
                          padding: "8px 12px",
                          backgroundColor: isOverSoftLimit ? "#fff3e0" : "#f5f5f5",
                          borderRadius: 4,
                          fontSize: 13,
                          border: isOverSoftLimit
                            ? "1px solid #ffb74d"
                            : "none",
                        }}
                      >
                        <span>
                          📱 SMS 送信時の見積もり
                          {editing.useUrlPage
                            ? " (URL 短縮ページ含む)"
                            : " (プレースホルダ展開想定)"}
                        </span>
                        <span style={{ color: "#555" }}>
                          本文展開後: <strong>{estimatedLength}</strong>{" "}
                          {editing.useUrlPage ? `/ ${URL_SMS_SOFT_LIMIT}` : ""} 文字
                        </span>
                        <span style={{ color: "#555" }}>
                          送信通数: <strong>{segments}</strong> 通 (70 文字単位)
                        </span>
                        <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                          費用: ¥{cost.toLocaleString()}
                        </span>
                        {isOverSoftLimit && (
                          <span
                            style={{
                              color: "#e65100",
                              fontWeight: "bold",
                              flexBasis: "100%",
                            }}
                          >
                            ⚠️ {URL_SMS_SOFT_LIMIT} 文字を超えています。 SMS が
                            2 通分課金になる可能性があります (本文を短くするか URL 短縮を活用してください)
                          </span>
                        )}
                      </Box>
                    );
                  })()
                ) : null}
                {editing.audience === "customer" && (
                  <Box
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 4,
                      padding: 12,
                      backgroundColor: editing.useUrlPage ? "#f5fafc" : "#fff",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editing.useUrlPage}
                          disabled={!canUseUrlPage}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              useUrlPage: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                      label={
                        canUseUrlPage ? (
                          <>
                            <strong>詳細を URL 先ページで表示する</strong> (SMS
                            費用 60% 削減、 顧客が URL をタップして詳細閲覧)
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            <strong>詳細を URL 先ページで表示する</strong> —{" "}
                            <span style={{ color: "#1976d2" }}>
                              {companyBusinessType !== "visit"
                                ? "現在は来店型業態のみご利用可能 (デリバリー型は MCA サポートまで)"
                                : "starter / premium プランで利用可能"}
                            </span>
                          </Typography>
                        )
                      }
                    />
                    {editing.useUrlPage && canUseUrlPage && (
                      <Box mt={1} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <TextField
                          label="URL 先ページタイトル (任意)"
                          value={editing.urlPageTitle}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              urlPageTitle: e.target.value,
                            })
                          }
                          fullWidth
                          inputProps={{ maxLength: 255 }}
                          placeholder="例: ご予約内容のご案内"
                        />
                        <TextField
                          label="URL 先ページ本文 (プレースホルダ展開可、 文字数制限なし)"
                          value={editing.urlPageBody}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              urlPageBody: e.target.value,
                            })
                          }
                          onFocus={() => setFocusedField("urlPageBody")}
                          fullWidth
                          multiline
                          rows={18}
                          inputRef={urlPageBodyRef}
                          placeholder={
                            "例:\n{{guestName}} 様\n\nご予約内容:\n日時: {{orderPlanInTime}}\nコース: {{courseName}}\n担当: {{castName}}\n\nご来店お待ちしております。"
                          }
                        />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          ※ URL 先ページは送信 48 時間後に自動削除されます (個人情報保護)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={editing.isDefault}
                      onChange={(e) =>
                        setEditing({ ...editing, isDefault: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="このテンプレートを既定にする (同 audience 内 1 件)"
                />
              </Box>

              {/* 右ペイン: プレースホルダ パネル (固定縦スクロール) */}
              <Box
                flex={1}
                style={{
                  borderLeft: "1px solid #e0e0e0",
                  paddingLeft: 16,
                  overflowY: "auto",
                  maxHeight: 800,
                  minWidth: 280,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  プレースホルダ挿入
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  本文 / 件名のカーソル位置に挿入されます。
                </Typography>
                {editing.useUrlPage && canUseUrlPage && (
                  <Box mt={2}>
                    <Typography
                      variant="caption"
                      style={{ fontWeight: "bold", color: "#0277bd" }}
                    >
                      🔗 URL 短縮
                    </Typography>
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: 4, marginTop: 4 }}
                    >
                      <Chip
                        label="短縮 URL"
                        size="small"
                        style={{
                          backgroundColor: "#0277bd",
                          color: "#fff",
                        }}
                        onClick={() => {
                          if (!editing) return;
                          if (focusedField === "urlPageBody") {
                            window.alert(
                              "URL プレースホルダは SMS 本文 (上の欄) に挿入してください。 URL 先ページ本文には不要です。"
                            );
                            return;
                          }
                          const el = bodyRef.current;
                          const token = URL_PAGE_PLACEHOLDER;
                          if (!el) {
                            setEditing({ ...editing, body: editing.body + token });
                            return;
                          }
                          const start = el.selectionStart ?? editing.body.length;
                          const end = el.selectionEnd ?? editing.body.length;
                          const next =
                            editing.body.slice(0, start) + token + editing.body.slice(end);
                          setEditing({ ...editing, body: next });
                          setTimeout(() => {
                            el.focus();
                            el.selectionStart = start + token.length;
                            el.selectionEnd = start + token.length;
                          }, 0);
                        }}
                      />
                    </Box>
                  </Box>
                )}
                {FIELD_CATEGORIES.map((cat) => (
                  <Box key={cat.label} mt={2}>
                    <Typography
                      variant="caption"
                      style={{ fontWeight: "bold", color: "#555" }}
                    >
                      {cat.label}
                    </Typography>
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: 4, marginTop: 4 }}
                    >
                      {cat.fields.map((f) => (
                        <Chip
                          key={f.key}
                          label={f.label}
                          size="small"
                          onClick={() => insertPlaceholder(f.key)}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>キャンセル</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageTemplate;
